import { useCallback, useEffect, useMemo, useRef } from "react";
import { patchReplace } from "../../../helpers/patchDoc";
import { useInstruction } from "../contexts/InstructionContext";

export const incomeFrequencyOptions = [
    { label: "One-Off", value: 4, multiplier: 1 },
    { label: "Monthly", value: 0, multiplier: 12 },
    { label: "Quarterly", value: 1, multiplier: 4 },
    { label: "Annual", value: 2, multiplier: 1 },
    { label: "Biannual", value: 3, multiplier: 2 },
];

const getIncomeMultiplier = (frequency) =>
    incomeFrequencyOptions.find(opt => opt.value === frequency)?.multiplier ?? 1;

const useIncome = () => {
    const [{ patchInvest: patchInvestTrigger, realTimePatchInvest }, { instructionId, realTimeInstruction, invest, divest }, { calculateIncomeTotal, realTimeIncomeTotal, realTimeRemainder }] = useInstruction();

    const patchInvest = useCallback((property, value) =>
        patchInvestTrigger({ investId: invest?.id, instructionId, operations: [patchReplace(property, value)] }).unwrap(),
        [instructionId, invest?.id, patchInvestTrigger]);

    const realTimePatchInvestSingle = useCallback((property, value) => {
        let operations = [patchReplace(property, value)];

        realTimePatchInvest(operations);
    }, [realTimePatchInvest]);

    const cachedIncomeTotal = useMemo(() =>
        calculateIncomeTotal(invest?.clientWithdrawalAmount, invest?.earmarkCashAmount, invest?.taxFreeCashAmount, invest?.toCashAccount, invest?.retainAmount),
        [calculateIncomeTotal, invest]);

    const cachedRemainder = useMemo(() =>
        Math.max(divest?.totalSaleProceeds - cachedIncomeTotal, 0),
        [cachedIncomeTotal, divest?.totalSaleProceeds]);

    // For not overwriting custom Earmark amount when instruction first loads
    const isFirstRealTimeEarmarkUpdate = useRef(true);
    const isFirstOnBlurEarmarkUpdate = useRef(true);

    // Real-time update of earmarked amount when client income amount/frequency changes
    useEffect(() => {
        if (isFirstRealTimeEarmarkUpdate.current) {
            isFirstRealTimeEarmarkUpdate.current = false;
            return;
        }

        const updateRealTimeEarmarkTimeout = setTimeout(() => {
            const incomeAmount = realTimeInstruction?.invest?.clientIncomeAmount ?? 0;
            const incomeFrequency = realTimeInstruction?.invest?.clientIncomeFrequency;
            const incomeMultiplier = getIncomeMultiplier(incomeFrequency);
            const earmarkCashAmount = incomeAmount * incomeMultiplier;

            realTimePatchInvestSingle("earmarkCashAmount", earmarkCashAmount);
        }, 150);

        return () => clearTimeout(updateRealTimeEarmarkTimeout);
    }, [realTimeInstruction?.invest?.clientIncomeAmount, realTimeInstruction?.invest?.clientIncomeFrequency, realTimePatchInvestSingle]);

    // onBlur update of earmarked amount when client income amount/frequency changes
    useEffect(() => {
        // If the useEffect is triggered due to going from null to 0 (i.e, on first page load), skip the effect
        if (isFirstOnBlurEarmarkUpdate.current) {
            isFirstOnBlurEarmarkUpdate.current = false;
            return;
        }

        const updateIncomeTimeout = setTimeout(() => {
            const incomeAmount = invest?.clientIncomeAmount ?? 0;
            const incomeFrequency = invest?.clientIncomeFrequency;
            const incomeMultiplier = getIncomeMultiplier(incomeFrequency);
            const earmarkCashAmount = incomeAmount * incomeMultiplier;

            patchInvest("earmarkCashAmount", earmarkCashAmount);
        }, 150);

        return () => clearTimeout(updateIncomeTimeout);
    }, [invest?.clientIncomeAmount, invest?.clientIncomeFrequency, patchInvest]);

    // For not overwriting custom Inv. Amount when instruction first loads
    const isFirstRealTimeInvAmountUpdate = useRef(true);
    const isFirstOnBlurInvAmountUpdate = useRef(true);

    // Real-time update of total investment amount when values in this section change
    useEffect(() => {
        if (isFirstRealTimeInvAmountUpdate.current) {
            isFirstRealTimeInvAmountUpdate.current = false;
            return;
        }

        const updateRealTimeInvestmentAmountTimeout = setTimeout(() =>
            realTimePatchInvestSingle("totalInvestmentAmount", realTimeRemainder), 150);

        return () => clearTimeout(updateRealTimeInvestmentAmountTimeout);
    }, [realTimePatchInvestSingle, realTimeRemainder]);

    // onBlur update of total investment amount when values in this section change
    useEffect(() => {
        if (isFirstOnBlurInvAmountUpdate.current) {
            isFirstOnBlurInvAmountUpdate.current = false;
            return;
        }

        const updateInvestmentAmountTimeout = setTimeout(() =>
            patchInvest("totalInvestmentAmount", cachedRemainder), 150);

        return () => clearTimeout(updateInvestmentAmountTimeout);
    }, [cachedRemainder, patchInvest]);

    const showTransferWarning = useMemo(() =>
        (Math.round(realTimeInstruction?.divest?.totalSaleProceeds * 100) / 100) - realTimeIncomeTotal - realTimeRemainder < 0,
        [realTimeInstruction?.divest?.totalSaleProceeds, realTimeIncomeTotal, realTimeRemainder]);

    return {
        patchInvest,
        realTimePatchInvest,
        realTimePatchInvestSingle,
        showTransferWarning
    }

}

export default useIncome;